import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Language } from '../constants/strings';
import { getUserProfile, UserProfile as UserProfileType, signOutUser, deleteUserProfile, isPlanDowngrade, removeAllCompanyMembers, getSubscriptionLimits, getCompanyProfile, updateUserOrganization, updateCompanyName, updateCompanyReportLanguage, updateCompanyLocalServer, updateCompanyPdfWorkflowMode, updateCompanyLogo, removeAllToolsFromCompany, saveUserProfile, changePassword, PdfWorkflowMode } from '../services/authService';
import { auth } from '../config/firebase';
import { logger } from '../utils/logger';
import { createCheckoutSession, verifyCheckoutSession, syncSubscriptionStatus, getSubscriptionDetails } from '../services/stripeService';
import { issueRegistrationToken } from '../services/localServerService';
import { cacheUserData } from '../services/cacheService';
import { DEFAULT_COMPANY_LOGO_URL } from '../constants/storage';
import { EngineeringLoadingScreen } from '../components/EngineeringLoadingScreen';
import '../../public/styles/main.css';

interface ProfilePageProps {
}

export default function ProfilePage({}: ProfilePageProps) {
    const { strings, setLanguage } = useLanguage();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [userProfile, setUserProfile] = useState<UserProfileType | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [userEmail, setUserEmail] = useState<string>('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deletePassword, setDeletePassword] = useState<string>('');
    const [deletePasswordError, setDeletePasswordError] = useState<string>('');
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [activePlanFromStripe, setActivePlanFromStripe] = useState<string | null>(null);
    const [showDowngradeWarning, setShowDowngradeWarning] = useState(false);
    const [pendingPlanId, setPendingPlanId] = useState<string | null>(null);
    const [editableOrganization, setEditableOrganization] = useState<string>('');
    const [isSavingOrganization, setIsSavingOrganization] = useState(false);
    const [organizationSaveError, setOrganizationSaveError] = useState<string>('');
    // Local server (on-prem) settings — manager only
    const [editableLocalServerUrl, setEditableLocalServerUrl] = useState<string>('');
    const [pdfWorkflowMode, setPdfWorkflowMode] = useState<PdfWorkflowMode>('localServer');
    const [isSavingPdfWorkflow, setIsSavingPdfWorkflow] = useState(false);
    const [localServerSaveError, setLocalServerSaveError] = useState<string>('');
    const [localServerSaveSuccess, setLocalServerSaveSuccess] = useState<boolean>(false);
    /** Registration token UI: token issued by the cloud backend for this company. */
    const [isIssuingToken, setIsIssuingToken] = useState<boolean>(false);
    const [issuedToken, setIssuedToken] = useState<string | null>(null);
    const [issuedTokenExpiresAt, setIssuedTokenExpiresAt] = useState<number | null>(null);
    const [tokenIssueError, setTokenIssueError] = useState<string>('');
    const [tokenCopied, setTokenCopied] = useState<boolean>(false);
    const [companyIdCopied, setCompanyIdCopied] = useState<boolean>(false);
    const [companyProfile, setCompanyProfile] = useState<any>(null);
    const [showRemoveToolsConfirm, setShowRemoveToolsConfirm] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState<Language>('en');
    const [selectedReportLanguage, setSelectedReportLanguage] = useState<Language>('en');
    const [isSavingLanguage, setIsSavingLanguage] = useState(false);
    const [logoFile, setLogoFile] = useState<File | null>(null);
    const [useDefaultLogo, setUseDefaultLogo] = useState(false);
    const [logoError, setLogoError] = useState<string>('');
    const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
    const logoFileInputRef = useRef<HTMLInputElement | null>(null);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [currentPassword, setCurrentPassword] = useState<string>('');
    const [newPassword, setNewPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [passwordChangeError, setPasswordChangeError] = useState<string>('');

    useEffect(() => {
        // Manage object URL for selected logo preview
        if (!logoFile) {
            setLogoPreviewUrl(null);
            return;
        }
        const objectUrl = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(objectUrl);
        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [logoFile]);

    useEffect(() => {
        const loadUserProfile = async () => {
            try {
                const currentUser = auth.currentUser;
                if (currentUser) {
                    setUserEmail(currentUser.email || '');
                    const profile = await getUserProfile(currentUser.uid);
                    if (profile) {
                        setUserProfile(profile);
                        
                        // Load language settings from profile
                        if (profile.language) {
                            setSelectedLanguage(profile.language as Language);
                        }
                        // Load report language from localStorage for user role
                        if (profile.role.toLowerCase() === 'user') {
                            const savedReportLanguage = localStorage.getItem('reportLanguage') as Language;
                            if (savedReportLanguage && (savedReportLanguage === 'et' || savedReportLanguage === 'en' || savedReportLanguage === 'ru')) {
                                setSelectedReportLanguage(savedReportLanguage);
                            }
                        }
                        
                        // Get plan from profile (synced on login)
                        // Note: Subscription sync happens only on login (checkpoint)
                        // During session, we use cached data from login sync
                        if (profile.role.toLowerCase() === 'manager' && profile.subscriptionId) {
                            // Try to get plan from cached sync data if available
                            const subscriptionLastSyncKey = `subscription_sync_${profile.subscriptionId}`;
                            const lastSyncTime = sessionStorage.getItem(subscriptionLastSyncKey);
                            
                            if (lastSyncTime) {
                                logger.log('[Profile] Using subscription data synced on login');
                                // Plan will be loaded from profile.selectedPlan which was synced on login
                            }
                        }
                        
                        // Load company profile
                        // For managers: load their own company profile
                        // For editors/users: load company profile by companyId stored in organization field
                        let companyId: string | null = null;
                        if (profile.role.toLowerCase() === 'manager') {
                            companyId = currentUser.uid; // Manager's companyId is their own uid
                        } else if (profile.organization) {
                            companyId = profile.organization; // For editors/users, organization stores companyId
                        }
                        
                        if (companyId) {
                            try {
                                const loadedCompanyProfile = await getCompanyProfile(companyId);
                                if (loadedCompanyProfile) {
                                    setCompanyProfile(loadedCompanyProfile);
                                    
                                    // For managers: set editable organization name from company profile
                                    if (profile.role.toLowerCase() === 'manager') {
                                        setEditableOrganization(loadedCompanyProfile.companyName || '');
                                        setEditableLocalServerUrl(loadedCompanyProfile.localServer?.url || '');
                                        if (loadedCompanyProfile.pdfWorkflowMode) {
                                            setPdfWorkflowMode(loadedCompanyProfile.pdfWorkflowMode);
                                        } else {
                                            setPdfWorkflowMode(loadedCompanyProfile.localServer?.enabled !== false ? 'localServer' : 'manual');
                                        }
                                    }
                                }
                            } catch (error) {
                                logger.warn('[Profile] Could not load company profile:', error);
                            }
                        }
                    }
                } else {
                    navigate('/login');
                }
            } catch (error) {
                logger.error('Error loading user profile:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadUserProfile();
    }, [navigate]);

    // Handle return from Stripe checkout
    useEffect(() => {
        const sessionId = searchParams.get('session_id');
        
        if (sessionId) {
            logger.log('[Profile] Payment callback detected:', { sessionId });
            
            setIsProcessingPayment(true);
            setIsLoading(true);
            
            verifyCheckoutSession(sessionId).then(async (verificationResult) => {
                logger.log('[Profile] Verification result:', verificationResult);
                
                if (!verificationResult.success || !verificationResult.session) {
                    logger.error('[Profile] Payment verification failed:', verificationResult);
                    setIsProcessingPayment(false);
                    setIsLoading(false);
                    // Remove session_id from URL
                    navigate('/profile', { replace: true });
                    return;
                }
                
                const paymentStatus = verificationResult.session.paymentStatus;
                logger.log('[Profile] Payment status:', paymentStatus);
                
                // Check if payment was successful
                if (paymentStatus === 'paid' || paymentStatus === 'complete') {
                    // Check if this is remove_all_tools action (one-time payment)
                    const action = searchParams.get('action');
                    if (action === 'remove_tools' && auth.currentUser) {
                        logger.log('[Profile] Remove all tools payment successful, removing tools...');
                        try {
                            await removeAllToolsFromCompany(auth.currentUser.uid);
                            // Navigate to dashboard after successful removal
                            navigate('/dashboard', { replace: true });
                        } catch (error: any) {
                            logger.error('[Profile] Error removing all tools:', error);
                            alert(`${strings.PROFILE_REMOVE_TOOLS_ERROR}: ${error.message || strings.PROFILE_UNKNOWN_ERROR}`);
                            setIsProcessingPayment(false);
                            setIsLoading(false);
                            navigate('/profile', { replace: true });
                        }
                        return;
                    }
                    
                    logger.log('[Profile] Payment successful, syncing subscription status...');
                    
                    // For subscription payments, sync subscription status
                    // One-time payments don't have subscription, so skip this part
                    if (verificationResult.session.subscription) {
                        const subscriptionId = typeof verificationResult.session.subscription === 'string' 
                            ? verificationResult.session.subscription 
                            : verificationResult.session.subscription;
                        
                        try {
                            logger.log('[Profile] Syncing subscription status after payment...');
                            const syncResult = await syncSubscriptionStatus(subscriptionId);
                            if (syncResult.success) {
                                // Update sync timestamp (user will be logged out after payment, so this prepares for next login)
                                const subscriptionLastSyncKey = `subscription_sync_${subscriptionId}`;
                                sessionStorage.setItem(subscriptionLastSyncKey, Date.now().toString());
                                
                                if (syncResult.subscription?.planId) {
                                    setActivePlanFromStripe(syncResult.subscription.planId);
                                }
                                
                                // Note: User will be logged out after plan change, so next login will sync fresh data
                            }
                        } catch (syncError) {
                            logger.warn('[Profile] Could not sync subscription status:', syncError);
                        }
                    }
                    
                    logger.log('[Profile] Logging out and redirecting...');
                    
                    // Log out manager and redirect to home page
                    try {
                        await signOutUser();
                        logger.log('[Profile] Manager logged out after successful subscription');
                        // Remove session_id from URL and redirect to home
                        navigate('/', { replace: true });
                    } catch (error) {
                        logger.error('[Profile] Error logging out:', error);
                        // Still redirect even if logout fails
                        navigate('/', { replace: true });
                    }
                } else {
                    // Payment was not completed
                    logger.warn('[Profile] Payment was not completed');
                    setIsProcessingPayment(false);
                    setIsLoading(false);
                    // Remove session_id from URL
                    navigate('/profile', { replace: true });
                }
            }).catch((error) => {
                logger.error('[Profile] Error verifying payment:', error);
                setIsProcessingPayment(false);
                setIsLoading(false);
                // Remove session_id from URL
                navigate('/profile', { replace: true });
            });
        }
    }, [searchParams, navigate]);

    const getRoleDisplayName = (role: string): string => {
        const roleLower = role.toLowerCase();
        switch (roleLower) {
            case 'admin':
                return strings.ROLE_ADMIN;
            case 'manager':
                return strings.ROLE_MANAGER;
            case 'editor':
                return strings.ROLE_EDITOR;
            case 'user':
                return strings.ROLE_USER;
            default:
                return role;
        }
    };

    const handleBack = () => {
        navigate('/dashboard');
    };

    const handleLogout = async () => {
        try {
            await signOutUser();
            navigate('/login');
        } catch (error) {
            logger.error('Error logging out:', error);
        }
    };

    const handleChangePasswordClick = () => {
        setShowChangePasswordModal(true);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordChangeError('');
    };

    const handleCancelChangePassword = () => {
        setShowChangePasswordModal(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setPasswordChangeError('');
    };

    const handleConfirmChangePassword = async () => {
        if (!currentPassword) {
            setPasswordChangeError(strings.PROFILE_PASSWORD_REQUIRED);
            return;
        }

        if (!newPassword) {
            setPasswordChangeError(strings.PROFILE_NEW_PASSWORD_REQUIRED);
            return;
        }

        if (!confirmPassword) {
            setPasswordChangeError(strings.PROFILE_CONFIRM_PASSWORD_REQUIRED);
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordChangeError(strings.PROFILE_PASSWORD_MISMATCH);
            return;
        }

        setIsChangingPassword(true);
        setPasswordChangeError('');

        try {
            await changePassword(currentPassword, newPassword);
            alert(strings.PROFILE_PASSWORD_CHANGED);
            setShowChangePasswordModal(false);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            logger.error('Error changing password:', error);
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                setPasswordChangeError(strings.PROFILE_WRONG_PASSWORD);
            } else if (error.code === 'auth/requires-recent-login') {
                setPasswordChangeError(strings.PROFILE_RELOGIN_REQUIRED);
            } else if (error.code === 'auth/weak-password') {
                setPasswordChangeError(strings.FIREBASE_WEAK_PASSWORD);
            } else {
                setPasswordChangeError(strings.PROFILE_PASSWORD_CHANGE_ERROR);
            }
        } finally {
            setIsChangingPassword(false);
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
        setDeletePassword('');
        setDeletePasswordError('');
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setDeletePassword('');
        setDeletePasswordError('');
    };

    const handleConfirmDelete = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser) {
            logger.error('No current user');
            return;
        }

        if (!deletePassword) {
            setDeletePasswordError(strings.PROFILE_PASSWORD_REQUIRED);
            return;
        }

        setIsDeleting(true);
        setDeletePasswordError('');
        try {
            await deleteUserProfile(currentUser.uid, deletePassword);
            await signOutUser();
            navigate('/login');
        } catch (error: any) {
            logger.error('Error deleting profile:', error);
            if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
                setDeletePasswordError(strings.PROFILE_WRONG_PASSWORD);
            } else if (error.code === 'auth/requires-recent-login') {
                setDeletePasswordError(strings.PROFILE_RELOGIN_REQUIRED);
            } else {
                setDeletePasswordError(strings.PROFILE_DELETE_ERROR);
            }
            setIsDeleting(false);
        }
    };

    // Check if delete button should be hidden for manager in production
    const shouldHideDeleteButton = (): boolean => {
        if (!userProfile) return false;
        const isManager = userProfile.role.toLowerCase() === 'manager';
        const isProduction = process.env.REACT_APP_ENV === 'production' || process.env.NODE_ENV === 'production';
        return isManager && isProduction;
    };

    // Check if user has active subscription
    const hasActiveSubscription = (): boolean => {
        if (!userProfile) return false;
        return userProfile.subscriptionStatus === 'active';
    };

    // Get active plan from Stripe (primary source) or fallback to DB
    const getActivePlan = (): string | null => {
        if (!userProfile || !hasActiveSubscription()) return null;
        
        // Primary source: Stripe
        if (activePlanFromStripe) {
            return activePlanFromStripe;
        }
        
        // Fallback: Database
        return userProfile.selectedPlan || null;
    };

    // Handle plan selection and checkout
    const handlePlanSelect = async (planId: string) => {
        if (!userProfile || !userEmail) {
            logger.error('Cannot select plan: missing user profile or email');
            return;
        }

        // Ultimate plan requires contact
        if (planId === 'ultimate') {
            navigate('/contact-us', { state: { from: 'profile' } });
            return;
        }

        // Check if this is a downgrade
        const currentPlan = getActivePlan();
        if (isPlanDowngrade(currentPlan, planId)) {
            // Show warning modal
            setPendingPlanId(planId);
            setShowDowngradeWarning(true);
            return;
        }

        // Proceed with checkout for upgrade or same plan
        await proceedWithCheckout(planId);
    };

    // Proceed with checkout after confirmation
    const proceedWithCheckout = async (planId: string) => {
        if (!userEmail) {
            logger.error('Cannot proceed: missing email');
            return;
        }

        setIsProcessingPayment(true);
        try {
            const successUrl = `${window.location.origin}/profile?session_id={CHECKOUT_SESSION_ID}`;
            const cancelUrl = `${window.location.origin}/profile`;

            const checkoutResult = await createCheckoutSession({
                planId: planId,
                customerEmail: userEmail,
                successUrl: successUrl,
                cancelUrl: cancelUrl,
                metadata: {
                    userId: auth.currentUser?.uid || '',
                    email: userEmail,
                    plan: planId
                }
            });

            if (checkoutResult.success && checkoutResult.url) {
                logger.log('[Profile] Redirecting to Stripe checkout:', checkoutResult.url);
                window.location.href = checkoutResult.url;
            } else {
                logger.error('[Profile] Checkout session creation failed:', checkoutResult.error);
                alert(checkoutResult.error || strings.CHECKOUT_SESSION_ERROR);
                setIsProcessingPayment(false);
            }
        } catch (error: any) {
            logger.error('[Profile] Error creating checkout session:', error);
            alert(error.message || strings.ERROR_OCCURRED);
            setIsProcessingPayment(false);
        }
    };

    // Handle remove all tools click - show confirmation modal
    const handleRemoveAllTools = () => {
        setShowRemoveToolsConfirm(true);
    };

    // Handle cancel remove tools
    const handleCancelRemoveTools = () => {
        setShowRemoveToolsConfirm(false);
    };

    // Handle confirm remove all tools with payment
    const handleConfirmRemoveAllTools = async () => {
        if (!userEmail || !auth.currentUser) {
            logger.error('Cannot remove tools: missing email or user');
            setShowRemoveToolsConfirm(false);
            return;
        }

        setShowRemoveToolsConfirm(false);
        setIsProcessingPayment(true);
        try {
            const successUrl = `${window.location.origin}/profile?session_id={CHECKOUT_SESSION_ID}&action=remove_tools`;
            const cancelUrl = `${window.location.origin}/profile`;

            // Create checkout session for remove all tools action
            // Using a special planId or metadata to identify this action
            const checkoutResult = await createCheckoutSession({
                planId: 'remove_all_tools', // Special identifier
                customerEmail: userEmail,
                successUrl: successUrl,
                cancelUrl: cancelUrl,
                metadata: {
                    userId: auth.currentUser.uid,
                    email: userEmail,
                    action: 'remove_all_tools',
                    amount: '1000' // 10 EUR in cents
                }
            });

            if (checkoutResult.success && checkoutResult.url) {
                logger.log('[Profile] Redirecting to Stripe checkout for remove all tools');
                window.location.href = checkoutResult.url;
            } else {
                logger.error('[Profile] Checkout session creation failed:', checkoutResult.error);
                alert(checkoutResult.error || strings.CHECKOUT_SESSION_ERROR);
                setIsProcessingPayment(false);
            }
        } catch (error: any) {
            logger.error('[Profile] Error creating checkout session for remove tools:', error);
            alert(error.message || strings.ERROR_OCCURRED);
            setIsProcessingPayment(false);
        }
    };

    // Handle downgrade confirmation
    const handleDowngradeConfirm = async () => {
        if (!pendingPlanId || !auth.currentUser) {
            setShowDowngradeWarning(false);
            setPendingPlanId(null);
            return;
        }

        try {
            // Remove all company members before proceeding
            logger.log('[Profile] Removing all company members before downgrade...');
            await removeAllCompanyMembers(auth.currentUser.uid);
            
            // Close modal and proceed with checkout
            setShowDowngradeWarning(false);
            const planId = pendingPlanId;
            setPendingPlanId(null);
            
            await proceedWithCheckout(planId);
        } catch (error: any) {
            logger.error('[Profile] Error removing company members:', error);
            alert(error.message || strings.ERROR_REMOVING_MEMBERS);
            setShowDowngradeWarning(false);
            setPendingPlanId(null);
        }
    };

    // Handle downgrade cancellation
    const handleDowngradeCancel = () => {
        setShowDowngradeWarning(false);
        setPendingPlanId(null);
    };

    const handleContactUs = () => {
        navigate('/contact-us', { state: { from: 'profile' } });
    };

    const handleLanguageChange = (newLanguage: Language) => {
        setSelectedLanguage(newLanguage);
    };

    const handleReportLanguageChange = (newReportLanguage: Language) => {
        setSelectedReportLanguage(newReportLanguage);
        // Save report language to localStorage immediately for user role
        if (userProfile && userProfile.role.toLowerCase() === 'user') {
            localStorage.setItem('reportLanguage', newReportLanguage);
            logger.log('[ProfilePage] Report language saved to localStorage immediately:', newReportLanguage);
        }
    };


    // Check if language settings have changed
    const hasLanguageChanges = () => {
        if (!userProfile) return false;
        const languageChanged = selectedLanguage !== (userProfile.language || 'en');
        // For user role, only check App language changes (report language is saved immediately)
        // For manager role, check both organization name and language changes
        if (userProfile.role.toLowerCase() === 'user') {
            return languageChanged;
        }
        return languageChanged;
    };

    /** Manager main Save: organization, logo, language, and local server URL (when workflow uses server). */
    const managerHasUnsavedProfileChanges = () => {
        if (!userProfile || userProfile.role.toLowerCase() !== 'manager') return false;
        const urlChanged =
            pdfWorkflowMode === 'localServer' &&
            editableLocalServerUrl.trim() !== (companyProfile?.localServer?.url || '').trim();
        return (
            editableOrganization.trim() !== (companyProfile?.companyName || '') ||
            hasLanguageChanges() ||
            !!logoFile ||
            urlChanged
        );
    };

    // Handle saving languages only (for non-managers)
    const handleSaveLanguagesOnly = async () => {
        if (!userProfile) return;
        
        setIsSavingLanguage(true);
        try {
            const updatedProfile: UserProfileType = {
                ...userProfile,
                language: selectedLanguage
            };
            await saveUserProfile(updatedProfile);
            setUserProfile(updatedProfile);
            setLanguage(selectedLanguage); // Update global language context
            logger.log('[ProfilePage] Language saved:', selectedLanguage);
        } catch (error) {
            logger.error('[ProfilePage] Error saving language:', error);
            alert(strings.PROFILE_ORGANIZATION_SAVE_ERROR || 'Error saving language');
        } finally {
            setIsSavingLanguage(false);
        }
    };

    // Handle saving languages for all roles
    const handleSaveLanguages = async () => {
        if (!userProfile) return;
        
        setIsSavingLanguage(true);
        try {
            // Save app language in user profile (report language is already saved in localStorage immediately)
            const updatedProfile: UserProfileType = {
                ...userProfile,
                language: selectedLanguage
            };
            await saveUserProfile(updatedProfile);
            setUserProfile(updatedProfile);
            setLanguage(selectedLanguage); // Update global language context
            
            logger.log('[ProfilePage] App language saved:', selectedLanguage);
        } catch (error) {
            logger.error('[ProfilePage] Error saving languages:', error);
            alert(strings.PROFILE_ORGANIZATION_SAVE_ERROR || 'Error saving languages');
        } finally {
            setIsSavingLanguage(false);
        }
    };

    const handleSaveOrganization = async () => {
        const currentUser = auth.currentUser;
        if (!currentUser || !userProfile) {
            logger.error('Cannot save organization: missing user');
            return;
        }

        // Only managers can change organization name
        if (userProfile.role.toLowerCase() !== 'manager') {
            logger.error('Cannot save organization: only managers can change organization name');
            setOrganizationSaveError('Ainult juhatajad saavad muuta organisatsiooni nime');
            return;
        }

        setIsSavingOrganization(true);
        setOrganizationSaveError('');
        setLocalServerSaveError('');
        setLocalServerSaveSuccess(false);

        if (pdfWorkflowMode === 'localServer') {
            const trimmedUrl = editableLocalServerUrl.trim();
            if (trimmedUrl && !/^https?:\/\//i.test(trimmedUrl)) {
                setLocalServerSaveError(strings.LOCAL_SERVER_INVALID_URL);
                setIsSavingOrganization(false);
                return;
            }
        }

        try {
            // Update only company name in CompanyProfile
            // Note: organization field in UserProfile stores companyId, not company name
            // So we only update companyName in CompanyProfile
            await updateCompanyName(currentUser.uid, editableOrganization.trim());

            // Update local company profile state
            if (companyProfile) {
                setCompanyProfile({
                    ...companyProfile,
                    companyName: editableOrganization.trim()
                });
            }

            // Save logo changes if any (manager only)
            if (logoFile) {
                const result = await updateCompanyLogo(currentUser.uid, logoFile, false);
                if (companyProfile) {
                    setCompanyProfile({
                        ...companyProfile,
                        logoUrl: result.logoUrl,
                        logoFileName: result.logoFileName
                    });
                }
                // Clear logo selection after save
                setLogoFile(null);
                setLogoPreviewUrl(null);
                setLogoError('');
                // Reset file input
                if (logoFileInputRef.current) {
                    logoFileInputRef.current.value = '';
                }
            }

            // Also save languages if they were changed
            if (hasLanguageChanges()) {
                // Save app language
                const updatedProfile: UserProfileType = {
                    ...userProfile,
                    language: selectedLanguage
                };
                await saveUserProfile(updatedProfile);
                setUserProfile(updatedProfile);
                setLanguage(selectedLanguage); // Update global language context
                
                logger.log('[ProfilePage] Languages saved:', { language: selectedLanguage });
            }

            if (pdfWorkflowMode === 'localServer') {
                const trimmedUrl = editableLocalServerUrl.trim();
                const prevUrl = (companyProfile?.localServer?.url || '').trim();
                if (trimmedUrl !== prevUrl) {
                    await updateCompanyLocalServer(currentUser.uid, trimmedUrl, true);
                    setCompanyProfile((prev: any) =>
                        prev
                            ? {
                                  ...prev,
                                  localServer: {
                                      ...(prev.localServer || {}),
                                      url: trimmedUrl,
                                      enabled: true
                                  }
                              }
                            : prev
                    );
                    setLocalServerSaveSuccess(true);
                }
            }

            // Refresh cached user data (including logo data URL) so reports update immediately
            try {
                await cacheUserData(currentUser.uid);
            } catch (cacheError) {
                logger.warn('[Profile] Failed to refresh cache after saving profile:', cacheError);
            }

            logger.log('[Profile] Organization name saved successfully');
        } catch (error: any) {
            logger.error('[Profile] Error saving organization:', error);
            setOrganizationSaveError(strings.PROFILE_ORGANIZATION_SAVE_ERROR);
        } finally {
            setIsSavingOrganization(false);
        }
    };

    const handlePdfWorkflowModeChange = async (newMode: PdfWorkflowMode) => {
        const currentUser = auth.currentUser;
        if (!currentUser || userProfile?.role.toLowerCase() !== 'manager') {
            return;
        }
        if (newMode === pdfWorkflowMode) {
            return;
        }

        setIsSavingPdfWorkflow(true);
        setLocalServerSaveError('');
        setLocalServerSaveSuccess(false);
        try {
            await updateCompanyPdfWorkflowMode(currentUser.uid, newMode);
            if (newMode === 'localServer') {
                const trimmedUrl = editableLocalServerUrl.trim();
                await updateCompanyLocalServer(currentUser.uid, trimmedUrl, true);
            }
            setPdfWorkflowMode(newMode);
            if (companyProfile) {
                setCompanyProfile({
                    ...companyProfile,
                    pdfWorkflowMode: newMode,
                    localServer: {
                        ...(companyProfile.localServer || {}),
                        url: newMode === 'localServer' ? editableLocalServerUrl.trim() : (companyProfile.localServer?.url || ''),
                        enabled: newMode === 'localServer'
                    }
                });
            }
            try {
                await cacheUserData(currentUser.uid);
            } catch (cacheError) {
                logger.warn('[Profile] Failed to refresh cache after PDF workflow mode change:', cacheError);
            }
        } catch (error: unknown) {
            logger.error('[Profile] Error saving PDF workflow mode:', error);
            setLocalServerSaveError(strings.LOCAL_SERVER_SAVE_ERROR);
        } finally {
            setIsSavingPdfWorkflow(false);
        }
    };

    /**
     * Manager-only: ask the cloud backend to mint a one-time registration token
     * for the local server installer. The plaintext token is returned ONCE here
     * and shown so the manager can hand it to the IT engineer; the backend
     * stores only its hash. Single-use, expires in ~24h.
     */
    const handleIssueRegistrationToken = async () => {
        setIsIssuingToken(true);
        setTokenIssueError('');
        setTokenCopied(false);
        setIssuedToken(null);
        setIssuedTokenExpiresAt(null);
        try {
            const result = await issueRegistrationToken();
            setIssuedToken(result.token);
            setIssuedTokenExpiresAt(result.expiresAt);
        } catch (error: unknown) {
            // Do NOT log the token itself — it's a credential.
            logger.error('[Profile] Failed to issue registration token');
            const message = error instanceof Error && error.message
                ? error.message
                : strings.LOCAL_SERVER_TOKEN_ERROR;
            setTokenIssueError(message);
        } finally {
            setIsIssuingToken(false);
        }
    };

    const handleCopyRegistrationToken = async () => {
        if (!issuedToken) return;
        try {
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                await navigator.clipboard.writeText(issuedToken);
                setTokenCopied(true);
                window.setTimeout(() => setTokenCopied(false), 2000);
            }
        } catch {
            // Clipboard API unavailable / blocked — user can still select & copy manually.
        }
    };

    const handleCopyManagerCompanyId = async () => {
        const uid = auth.currentUser?.uid;
        if (!uid) return;
        try {
            if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
                await navigator.clipboard.writeText(uid);
                setCompanyIdCopied(true);
                window.setTimeout(() => setCompanyIdCopied(false), 2000);
            }
        } catch {
            // Clipboard API unavailable / blocked — user can still select & copy manually.
        }
    };

    // Show loading screen if loading or processing payment
    if (isLoading || isProcessingPayment) {
        return (
            <EngineeringLoadingScreen
                ariaLabel={isProcessingPayment ? strings.PROFILE_PROCESSING_PAYMENT : strings.PROFILE_LOADING}
            />
        );
    }

    if (!userProfile) {
        return (
            <div className="container">
                <div className="error-message">
                    {strings.PROFILE_LOADING_FAILED}
                </div>
                <button className="profile-back-btn" onClick={handleBack} aria-label={strings.PROFILE_BACK}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>
        );
    }

    return (
        <div className="container css-test-loaded">
            <h1 className="dashboard-main-title">QUALITY DATUM</h1>
            <button className="profile-back-btn" onClick={handleBack} aria-label={strings.PROFILE_BACK}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>
            
            <div className="profile-container">
                <h2 className="profile-title">{strings.PROFILE_TITLE}</h2>
                
                <div className="profile-content">
                    <div className="profile-section">
                        <div className="profile-field">
                            <label className="profile-label">{strings.PROFILE_NAME_LABEL}</label>
                            <div className="profile-value">{userProfile.displayName || '-'}</div>
                        </div>
                        
                        <div className="profile-field">
                            <label className="profile-label">{strings.PROFILE_EMAIL_LABEL}</label>
                            <div className="profile-value">{userEmail || '-'}</div>
                        </div>
                        
                        <div className="profile-field">
                            <label className="profile-label">{strings.PROFILE_EMAIL_VERIFIED_LABEL}</label>
                            <div className="profile-value">
                                {userProfile.emailVerified ? strings.YES : strings.NO}
                            </div>
                        </div>
                        
                        <div className="profile-field">
                            <label className="profile-label">{strings.PROFILE_ROLE_LABEL}</label>
                            <div className="profile-value">{getRoleDisplayName(userProfile.role)}</div>
                        </div>
                        
                        <div className="profile-field">
                            <label className="profile-label">{strings.PROFILE_ORGANIZATION_LABEL}</label>
                            {userProfile.role.toLowerCase() === 'manager' ? (
                                <>
                                    <input
                                        type="text"
                                        value={editableOrganization}
                                        onChange={(e) => {
                                            setEditableOrganization(e.target.value);
                                            setOrganizationSaveError('');
                                        }}
                                        className="profile-input"
                                        placeholder={strings.PROFILE_ORGANIZATION_PLACEHOLDER}
                                        disabled={isSavingOrganization}
                                    />
                                    {organizationSaveError && (
                                        <div className="profile-error-message">{organizationSaveError}</div>
                                    )}
                                </>
                            ) : (
                                <div className="profile-value">{companyProfile?.companyName || '-'}</div>
                            )}
                        </div>

                        {userProfile.role.toLowerCase() === 'manager' && (
                            <div className="profile-field">
                                <label className="profile-label">{strings.PROFILE_LOGO_LABEL}</label>
                                <div className="profile-logo-row">
                                    <img
                                        className="profile-logo-preview"
                                        src={logoPreviewUrl || companyProfile?.logoUrl || DEFAULT_COMPANY_LOGO_URL}
                                        alt={strings.PROFILE_LOGO_LABEL}
                                    />
                                    <div className="profile-logo-actions">
                                        <button
                                            type="button"
                                            className="profile-secondary-btn"
                                            onClick={() => logoFileInputRef.current?.click()}
                                        >
                                            {strings.PROFILE_LOGO_CHANGE}
                                        </button>
                                        <input
                                            ref={logoFileInputRef}
                                            className="profile-file-input"
                                            type="file"
                                            accept="image/*"
                                            style={{ display: 'none' }}
                                            onChange={(e) => {
                                                const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                                                if (!file) {
                                                    return;
                                                }
                                                setLogoFile(file);
                                                setLogoError('');
                                                // Create preview URL
                                                const reader = new FileReader();
                                                reader.onloadend = () => {
                                                    setLogoPreviewUrl(reader.result as string);
                                                };
                                                reader.readAsDataURL(file);
                                                // Reset input so selecting the same file again still triggers onChange
                                                e.currentTarget.value = '';
                                            }}
                                        />
                                    </div>
                                </div>
                                {logoError && <div className="profile-error-message">{logoError}</div>}
                            </div>
                        )}

                        {userProfile.role.toLowerCase() === 'manager' && (
                            <div className="profile-field">
                                <label className="profile-label">{strings.PROFILE_PDF_WORKFLOW_LABEL}</label>
                                <div
                                    className="profile-segment-buttons"
                                    role="group"
                                    aria-label={strings.PROFILE_PDF_WORKFLOW_LABEL}
                                >
                                    <button
                                        type="button"
                                        className={`profile-segment-btn ${pdfWorkflowMode === 'manual' ? 'profile-segment-btn-active' : ''}`}
                                        onClick={() => void handlePdfWorkflowModeChange('manual')}
                                        disabled={isSavingPdfWorkflow || isSavingOrganization}
                                    >
                                        {strings.PROFILE_PDF_WORKFLOW_MANUAL}
                                    </button>
                                    <button
                                        type="button"
                                        className={`profile-segment-btn ${pdfWorkflowMode === 'cloudDb' ? 'profile-segment-btn-active' : ''}`}
                                        onClick={() => void handlePdfWorkflowModeChange('cloudDb')}
                                        disabled={isSavingPdfWorkflow || isSavingOrganization}
                                    >
                                        {strings.PROFILE_PDF_WORKFLOW_CLOUD_DB}
                                    </button>
                                    <button
                                        type="button"
                                        className={`profile-segment-btn ${pdfWorkflowMode === 'localServer' ? 'profile-segment-btn-active' : ''}`}
                                        onClick={() => void handlePdfWorkflowModeChange('localServer')}
                                        disabled={isSavingPdfWorkflow || isSavingOrganization}
                                    >
                                        {strings.PROFILE_PDF_WORKFLOW_LOCAL_SERVER}
                                    </button>
                                </div>
                                {pdfWorkflowMode === 'manual' && (
                                    <p className="profile-pdf-workflow-hint">{strings.PROFILE_PDF_WORKFLOW_MANUAL_HELP}</p>
                                )}
                                {pdfWorkflowMode === 'cloudDb' && (
                                    <p className="profile-pdf-workflow-hint">{strings.PROFILE_PDF_WORKFLOW_CLOUD_DB_HELP}</p>
                                )}
                            </div>
                        )}

                        {userProfile.role.toLowerCase() === 'manager' && localServerSaveError && (
                            <div className="profile-field">
                                <div className="profile-error-message">{localServerSaveError}</div>
                            </div>
                        )}

                        {userProfile.role.toLowerCase() === 'manager' && pdfWorkflowMode === 'localServer' && (
                            <div className="profile-field">
                                <label className="profile-label">{strings.LOCAL_SERVER_URL_LABEL}</label>
                                <input
                                    type="text"
                                    value={editableLocalServerUrl}
                                    onChange={(e) => {
                                        setEditableLocalServerUrl(e.target.value);
                                        setLocalServerSaveError('');
                                        setLocalServerSaveSuccess(false);
                                    }}
                                    className="profile-input"
                                    placeholder="https://acme.qualitydatum.com"
                                    disabled={isSavingOrganization}
                                />
                                {companyProfile?.localServer?.lastSeenAt && (
                                    <p className="profile-pdf-workflow-hint">
                                        {strings.LOCAL_SERVER_LAST_SEEN}{new Date(companyProfile.localServer.lastSeenAt).toLocaleString()}
                                        {companyProfile.localServer.version
                                            ? `${strings.LOCAL_SERVER_VERSION_PREFIX}${companyProfile.localServer.version})`
                                            : ''}
                                    </p>
                                )}
                                {!companyProfile?.localServer?.lastSeenAt && editableLocalServerUrl && (
                                    <p className="profile-pdf-workflow-hint">{strings.LOCAL_SERVER_NEVER_SEEN}</p>
                                )}
                                {localServerSaveSuccess && (
                                    <p className="profile-pdf-workflow-hint profile-pdf-workflow-hint--success">
                                        {strings.LOCAL_SERVER_SAVED}
                                    </p>
                                )}
                            </div>
                        )}

                        {userProfile.role.toLowerCase() === 'manager' && pdfWorkflowMode === 'localServer' && auth.currentUser?.uid && (
                            <div className="profile-field">
                                <label className="profile-label">{strings.LOCAL_SERVER_COMPANY_ID_LABEL}</label>
                                <input
                                    type="text"
                                    className="profile-input"
                                    value={auth.currentUser.uid}
                                    readOnly
                                    onFocus={(e) => e.currentTarget.select()}
                                    aria-label={strings.LOCAL_SERVER_COMPANY_ID_LABEL}
                                />
                                <button
                                    type="button"
                                    className="profile-secondary-btn"
                                    onClick={() => void handleCopyManagerCompanyId()}
                                >
                                    {companyIdCopied ? strings.LOCAL_SERVER_TOKEN_COPIED : strings.LOCAL_SERVER_TOKEN_COPY}
                                </button>
                                <p className="profile-pdf-workflow-hint">{strings.LOCAL_SERVER_COMPANY_ID_HINT}</p>
                            </div>
                        )}

                        {userProfile.role.toLowerCase() === 'manager' && pdfWorkflowMode === 'localServer' && (
                            <div className="profile-field">
                                <label className="profile-label">{strings.LOCAL_SERVER_TOKEN_LABEL}</label>
                                <button
                                    type="button"
                                    className="profile-secondary-btn"
                                    onClick={() => void handleIssueRegistrationToken()}
                                    disabled={isIssuingToken}
                                >
                                    {isIssuingToken ? strings.LOCAL_SERVER_TOKEN_BTN_LOADING : strings.LOCAL_SERVER_TOKEN_BTN}
                                </button>
                                <p className="profile-pdf-workflow-hint">{strings.LOCAL_SERVER_TOKEN_HINT}</p>
                                {tokenIssueError && (
                                    <div className="profile-error-message">{tokenIssueError}</div>
                                )}
                                {issuedToken && (
                                    <>
                                        <input
                                            type="text"
                                            className="profile-input"
                                            value={issuedToken}
                                            readOnly
                                            onFocus={(e) => e.currentTarget.select()}
                                            aria-label={strings.LOCAL_SERVER_TOKEN_LABEL}
                                        />
                                        <button
                                            type="button"
                                            className="profile-secondary-btn"
                                            onClick={() => void handleCopyRegistrationToken()}
                                        >
                                            {tokenCopied ? strings.LOCAL_SERVER_TOKEN_COPIED : strings.LOCAL_SERVER_TOKEN_COPY}
                                        </button>
                                        {issuedTokenExpiresAt && (
                                            <p className="profile-pdf-workflow-hint">
                                                {strings.LOCAL_SERVER_TOKEN_EXPIRES_AT}
                                                {new Date(issuedTokenExpiresAt).toLocaleString()}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {userProfile.selectedPlan && (
                            <div className="profile-field">
                                <label className="profile-label">{strings.PROFILE_SUBSCRIPTION_PLAN_LABEL}</label>
                                <div className="profile-value">{userProfile.selectedPlan}</div>
                            </div>
                        )}
                        
                        <div className="profile-field">
                            <label className="profile-label">{strings.PROFILE_LANGUAGE_LABEL}</label>
                            <div className="language-buttons">
                                <button
                                    type="button"
                                    className={`language-flag-btn ${selectedLanguage === 'et' ? 'language-flag-btn-active' : ''}`}
                                    onClick={() => handleLanguageChange('et')}
                                >
                                    ET
                                </button>
                                <button
                                    type="button"
                                    className={`language-flag-btn ${selectedLanguage === 'en' ? 'language-flag-btn-active' : ''}`}
                                    onClick={() => handleLanguageChange('en')}
                                >
                                    EN
                                </button>
                                <button
                                    type="button"
                                    className={`language-flag-btn ${selectedLanguage === 'ru' ? 'language-flag-btn-active' : ''}`}
                                    onClick={() => handleLanguageChange('ru')}
                                >
                                    RU
                                </button>
                            </div>
                        </div>
                        
                        {userProfile.role.toLowerCase() === 'user' && (
                            <div className="profile-field">
                                <label className="profile-label">{strings.PROFILE_REPORT_LANGUAGE_LABEL}</label>
                                <div className="language-buttons">
                                    <button
                                        type="button"
                                        className={`language-flag-btn ${selectedReportLanguage === 'et' ? 'language-flag-btn-active' : ''}`}
                                        onClick={() => handleReportLanguageChange('et')}
                                    >
                                        ET
                                    </button>
                                    <button
                                        type="button"
                                        className={`language-flag-btn ${selectedReportLanguage === 'en' ? 'language-flag-btn-active' : ''}`}
                                        onClick={() => handleReportLanguageChange('en')}
                                    >
                                        EN
                                    </button>
                                    <button
                                        type="button"
                                        className={`language-flag-btn ${selectedReportLanguage === 'ru' ? 'language-flag-btn-active' : ''}`}
                                        onClick={() => handleReportLanguageChange('ru')}
                                    >
                                        RU
                                    </button>
                                </div>
                            </div>
                        )}
                        

                        {userProfile.role.toLowerCase() === 'manager' 

                        // SWITCH (comment/uncomment) - PLAN CARDS START

                        // && (
                        //     <>
                        //         <h1 className="pricing-plans-title" style={{ width: '100%', textAlign: 'center', color: '#1a365d', marginTop: '2rem' }}>
                        //             {strings.YOUR_PLAN_TITLE}
                        //         </h1>
                                
                        //         <div className="pricing-plans-container">
                        //             <div className={`pricing-card ${getActivePlan() === 'basic' ? 'selected' : ''}`}>
                        //                 <div className="pricing-card-header">
                        //                     <div className="pricing-card-title">{strings.BASIC_PLAN_TITLE}</div>
                        //                     <div className="pricing-card-price">60</div>
                        //                     <div className="pricing-card-period">{strings.MONTHLY_PRICE}</div>
                        //                 </div>
                                        
                        //                 <ul className="pricing-card-features">
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">1</span> {strings.EDITORS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">5</span> {strings.USERS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">3</span> {strings.MEASUREMENT_TOOLS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-text pricing-card-feature-comment">
                        //                             <span className="pricing-card-feature-highlight pricing-card-feature-comment">5</span> {strings.ADDITIONAL_TOOL_PRICE}
                        //                         </span>
                        //                     </li>
                        //                 </ul>
                                        
                        //                 <button
                        //                     type="button"
                        //                     className="pricing-card-button"
                        //                     onClick={() => handlePlanSelect('basic')}
                        //                     disabled={isProcessingPayment || getActivePlan() === 'basic'}
                        //                 >
                        //                     {getActivePlan() === 'basic' ? strings.PLAN_ACTIVATED_BUTTON : strings.SELECT_PLAN_BUTTON}
                        //                 </button>
                        //             </div>

                        //             <div className={`pricing-card ${getActivePlan() === 'premium' ? 'selected' : ''}`}>
                        //                 <div className="pricing-card-header">
                        //                     <div className="pricing-card-title">{strings.PREMIUM_PLAN_TITLE}</div>
                        //                     <div className="pricing-card-price">240</div>
                        //                     <div className="pricing-card-period">{strings.MONTHLY_PRICE}</div>
                        //                 </div>
                                        
                        //                 <ul className="pricing-card-features">
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">5</span> {strings.EDITORS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">30</span> {strings.USERS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">{getSubscriptionLimits('premium').toolLimit === 'unlimited' ? strings.UNLIMITED_LABEL : getSubscriptionLimits('premium').toolLimit}</span> {strings.MEASUREMENT_TOOLS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-text pricing-card-feature-comment">
                        //                             <span className="pricing-card-feature-highlight pricing-card-feature-comment">5</span> {strings.ADDITIONAL_TOOL_PRICE}
                        //                         </span>
                        //                     </li>
                        //                 </ul>
                                        
                        //                 <button
                        //                     type="button"
                        //                     className="pricing-card-button"
                        //                     onClick={() => handlePlanSelect('premium')}
                        //                     disabled={isProcessingPayment || getActivePlan() === 'premium'}
                        //                 >
                        //                     {getActivePlan() === 'premium' ? strings.PLAN_ACTIVATED_BUTTON : strings.SELECT_PLAN_BUTTON}
                        //                 </button>
                        //             </div>

                        //             <div className={`pricing-card ${getActivePlan() === 'ultimate' ? 'selected' : ''}`}>
                        //                 <div className="pricing-card-header">
                        //                     <div className="pricing-card-title">{strings.ULTIMATE_PLAN_TITLE}</div>
                        //                     <div className="pricing-card-price">-</div>
                        //                     <div className="pricing-card-period">{strings.MONTHLY_PRICE}</div>
                        //                 </div>
                                        
                        //                 <ul className="pricing-card-features">
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">?</span> {strings.EDITORS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">?</span> {strings.USERS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                     <li className="pricing-card-feature">
                        //                         <span className="pricing-card-feature-icon">✓</span>
                        //                         <span className="pricing-card-feature-text">
                        //                             <span className="pricing-card-feature-highlight">?</span> {strings.MEASUREMENT_TOOLS_LABEL}
                        //                         </span>
                        //                     </li>
                        //                 </ul>
                                        
                        //                 <button
                        //                     type="button"
                        //                     className="pricing-card-button-contact"
                        //                     onClick={handleContactUs}
                        //                     disabled={isProcessingPayment}
                        //                 >
                        //                     {strings.CONTACT_US_BUTTON}
                        //                 </button>
                        //             </div>
                        //         </div>
                        //      </>
                        // )

                        // PLAN CARDS END

                        }
                        
                        {/* Remove All Tools Button */}
                        {userProfile.role.toLowerCase() === 'manager' 

                        // SWITCH (comment/uncomment) - REMOVE ALL TOOLS BUTTON
                        
                        // && (
                        //     <div className="remove-tools-section">
                        //         <div className="remove-tools-card">
                        //             <div className="remove-tools-header">
                        //                 <div className="remove-tools-title">{strings.REMOVE_TOOLS_TITLE}</div>
                        //                 <div className="remove-tools-price">10</div>
                        //                 <div className="remove-tools-period">EUR</div>
                        //             </div>
                        //             <div className="remove-tools-description">
                        //                 {strings.REMOVE_TOOLS_DESCRIPTION}
                        //             </div>
                        //             <button
                        //                 type="button"
                        //                 className="remove-tools-button"
                        //                 onClick={handleRemoveAllTools}
                        //                 disabled={isProcessingPayment}
                        //             >
                        //                 {isProcessingPayment ? strings.REMOVE_TOOLS_PROCESSING : strings.REMOVE_TOOLS_TITLE}
                        //             </button>
                        //         </div>
                        //     </div>
                        //    )

                        }
                        PLAN CARDS HERE
                    </div>
                </div>

                <div className="profile-actions">
                    {(userProfile.role.toLowerCase() === 'manager' || hasLanguageChanges()) && (
                        <button
                            type="button"
                            className="profile-save-btn"
                            onClick={userProfile.role.toLowerCase() === 'manager' ? handleSaveOrganization : handleSaveLanguages}
                            disabled={
                                isSavingOrganization ||
                                isSavingLanguage ||
                                (userProfile.role.toLowerCase() === 'manager' &&
                                    !managerHasUnsavedProfileChanges()) ||
                                (!hasLanguageChanges() && userProfile.role.toLowerCase() !== 'manager')
                            }
                            aria-label={strings.PROFILE_SAVE_ORGANIZATION}
                        >
                            {(isSavingOrganization || isSavingLanguage) ? strings.PROFILE_SAVING : strings.PROFILE_SAVE}
                        </button>
                    )}
                    <button 
                        className="btn-secondary" 
                        onClick={handleChangePasswordClick}
                        aria-label={strings.PROFILE_CHANGE_PASSWORD}
                    >
                        {strings.PROFILE_CHANGE_PASSWORD}
                    </button>
                    <button 
                        className="btn-secondary" 
                        onClick={handleLogout}
                        aria-label={strings.PROFILE_LOGOUT}
                    >
                        {strings.PROFILE_LOGOUT}
                    </button>
                    {!shouldHideDeleteButton() && (
                        <button 
                            className="btn-delete" 
                            onClick={handleDeleteClick}
                            disabled={isDeleting}
                            aria-label={strings.PROFILE_DELETE_PROFILE}
                        >
                            {isDeleting ? strings.PROFILE_DELETING : strings.PROFILE_DELETE_PROFILE}
                        </button>
                    )}
                </div>
            </div>

            {showDeleteConfirm && (
                <div className="modal-overlay">
                    <div className="modal-container delete-modal">
                        <div className="modal-header">
                            <h2>{strings.PROFILE_DELETE_CONFIRM_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text">
                                {strings.PROFILE_DELETE_CONFIRM_MESSAGE}
                            </p>
                            <p className="delete-warning-text">
                                {strings.PROFILE_DELETE_WARNING}
                            </p>
                            <div className="delete-password-field">
                                <label className="delete-password-label">
                                    {strings.PROFILE_PASSWORD_CONFIRM_LABEL}
                                </label>
                                <input
                                    type="password"
                                    value={deletePassword}
                                    onChange={(e) => {
                                        setDeletePassword(e.target.value);
                                        setDeletePasswordError('');
                                    }}
                                    disabled={isDeleting}
                                    placeholder={strings.PROFILE_PASSWORD_PLACEHOLDER}
                                    className={`delete-password-input ${deletePasswordError ? 'error' : ''}`}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !isDeleting && deletePassword) {
                                            handleConfirmDelete();
                                        }
                                    }}
                                />
                                {deletePasswordError && (
                                    <p className="delete-password-error">
                                        {deletePasswordError}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={handleCancelDelete}
                                disabled={isDeleting}
                            >
                                {strings.PROFILE_CANCEL}
                            </button>
                            <button 
                                className="btn-delete" 
                                onClick={handleConfirmDelete}
                                disabled={isDeleting || !deletePassword.trim()}
                            >
                                {isDeleting ? strings.PROFILE_DELETING : strings.PROFILE_DELETE}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showDowngradeWarning && (
                <div className="modal-overlay" onClick={handleDowngradeCancel}>
                    <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{strings.PLAN_DOWNGRADE_WARNING_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text">
                                {strings.PLAN_DOWNGRADE_WARNING_MESSAGE}
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={handleDowngradeCancel}
                                disabled={isProcessingPayment}
                            >
                                {strings.PLAN_DOWNGRADE_CANCEL_BUTTON}
                            </button>
                            <button 
                                className="btn-delete" 
                                onClick={handleDowngradeConfirm}
                                disabled={isProcessingPayment}
                            >
                                {strings.PLAN_DOWNGRADE_CONFIRM_BUTTON}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Remove All Tools Confirmation Modal */}
            {showRemoveToolsConfirm && (
                <div className="modal-overlay" onClick={handleCancelRemoveTools}>
                    <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{strings.REMOVE_TOOLS_CONFIRM_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text">
                                {strings.REMOVE_TOOLS_CONFIRM_MESSAGE}
                            </p>
                            <p className="delete-warning-text">
                                {strings.PROFILE_DELETE_WARNING}
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={handleCancelRemoveTools}
                                disabled={isProcessingPayment}
                            >
                                {strings.PROFILE_CANCEL}
                            </button>
                            <button 
                                className="btn-delete" 
                                onClick={handleConfirmRemoveAllTools}
                                disabled={isProcessingPayment}
                            >
                                {isProcessingPayment ? strings.REMOVE_TOOLS_PROCESSING : strings.REMOVE_TOOLS_CONFIRM}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Change Password Modal */}
            {showChangePasswordModal && (
                <div className="modal-overlay">
                    <div className="modal-container delete-modal">
                        <div className="modal-header">
                            <h2>{strings.PROFILE_CHANGE_PASSWORD_TITLE}</h2>
                        </div>
                        <div className="modal-body">
                            <p className="delete-confirmation-text">
                                {strings.PROFILE_CHANGE_PASSWORD_MESSAGE}
                            </p>
                            <div className="delete-password-field">
                                <label className="delete-password-label">
                                    {strings.PROFILE_CURRENT_PASSWORD_LABEL}
                                </label>
                                <input
                                    type="password"
                                    value={currentPassword}
                                    onChange={(e) => {
                                        setCurrentPassword(e.target.value);
                                        setPasswordChangeError('');
                                    }}
                                    disabled={isChangingPassword}
                                    placeholder={strings.PROFILE_CURRENT_PASSWORD_PLACEHOLDER}
                                    className={`delete-password-input ${passwordChangeError ? 'error' : ''}`}
                                />
                            </div>
                            <div className="delete-password-field">
                                <label className="delete-password-label">
                                    {strings.PROFILE_NEW_PASSWORD_LABEL}
                                </label>
                                <input
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => {
                                        setNewPassword(e.target.value);
                                        setPasswordChangeError('');
                                    }}
                                    disabled={isChangingPassword}
                                    placeholder={strings.PROFILE_NEW_PASSWORD_PLACEHOLDER}
                                    className={`delete-password-input ${passwordChangeError ? 'error' : ''}`}
                                />
                            </div>
                            <div className="delete-password-field">
                                <label className="delete-password-label">
                                    {strings.PROFILE_CONFIRM_NEW_PASSWORD_LABEL}
                                </label>
                                <input
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => {
                                        setConfirmPassword(e.target.value);
                                        setPasswordChangeError('');
                                    }}
                                    disabled={isChangingPassword}
                                    placeholder={strings.PROFILE_CONFIRM_NEW_PASSWORD_PLACEHOLDER}
                                    className={`delete-password-input ${passwordChangeError ? 'error' : ''}`}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !isChangingPassword && currentPassword && newPassword && confirmPassword) {
                                            handleConfirmChangePassword();
                                        }
                                    }}
                                />
                            </div>
                            {passwordChangeError && (
                                <p className="delete-password-error">
                                    {passwordChangeError}
                                </p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn-secondary" 
                                onClick={handleCancelChangePassword}
                                disabled={isChangingPassword}
                            >
                                {strings.PROFILE_CANCEL}
                            </button>
                            <button 
                                className="btn-delete" 
                                onClick={handleConfirmChangePassword}
                                disabled={isChangingPassword || !currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()}
                            >
                                {isChangingPassword ? strings.PROFILE_CHANGING_PASSWORD : strings.PROFILE_CHANGE_PASSWORD}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

